import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, BookOpen, Brain, Heart, Eye } from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";

// ─── Color Style Maps ────────────────────────────────────────────────────────
// Pre-defined Tailwind classes to avoid dynamic class purging issues.

type ColorKey = "blue" | "emerald" | "violet" | "amber" | "rose" | "sky";

const COLOR_STYLES: Record<ColorKey, {
  badge: string;
  text: string;
  bg: string;
  bgSubtle: string;
  border: string;
  borderAccent: string;
  barGradient: string;
}> = {
  blue: {
    badge: "bg-primary/10 text-primary",
    text: "text-primary",
    bg: "bg-primary",
    bgSubtle: "bg-primary/5/60",
    border: "border-primary/20/60",
    borderAccent: "border-primary",
    barGradient: "from-primary to-primary/60",
  },
  emerald: {
    badge: "bg-emerald-500/10 text-emerald-600",
    text: "text-emerald-600",
    bg: "bg-emerald-500",
    bgSubtle: "bg-emerald-50/60",
    border: "border-emerald-200/60",
    borderAccent: "border-emerald-500",
    barGradient: "from-emerald-500 to-emerald-400",
  },
  violet: {
    badge: "bg-violet-500/10 text-violet-600",
    text: "text-violet-600",
    bg: "bg-violet-500",
    bgSubtle: "bg-violet-50/60",
    border: "border-violet-200/60",
    borderAccent: "border-violet-500",
    barGradient: "from-violet-500 to-violet-400",
  },
  amber: {
    badge: "bg-amber-500/10 text-amber-600",
    text: "text-amber-600",
    bg: "bg-amber-500",
    bgSubtle: "bg-amber-50/60",
    border: "border-amber-200/60",
    borderAccent: "border-amber-500",
    barGradient: "from-amber-500 to-amber-400",
  },
  rose: {
    badge: "bg-rose-500/10 text-rose-600",
    text: "text-rose-600",
    bg: "bg-rose-500",
    bgSubtle: "bg-rose-50/60",
    border: "border-rose-200/60",
    borderAccent: "border-rose-500",
    barGradient: "from-rose-500 to-rose-400",
  },
  sky: {
    badge: "bg-sky-500/10 text-sky-600",
    text: "text-sky-600",
    bg: "bg-sky-500",
    bgSubtle: "bg-sky-50/60",
    border: "border-sky-200/60",
    borderAccent: "border-sky-500",
    barGradient: "from-sky-500 to-sky-400",
  },
};

// ─── Types ───────────────────────────────────────────────────────────────────

interface BlogPost {
  id: string;
  category: string;
  color: ColorKey;
  title: string;
  subtitle: string;
  readTime: string;
  heroIcon: string;
  content: string;
  keyInsight: string;
  sources: string;
}

interface WisdomDimension {
  name: string;
  icon: string;
  color: ColorKey;
  description: string;
  methods: string[];
  question: string;
}

interface Stat {
  value: string;
  label: string;
  icon: string;
}

interface Method {
  name: string;
  effectiveness: number;
  category: string;
  color: ColorKey;
}

interface MethodDeepDive {
  title: string;
  icon: string;
  color: ColorKey;
  description: string;
  howApplied: string;
  source: string;
}

// ─── Data ────────────────────────────────────────────────────────────────────

const BLOG_POSTS: BlogPost[] = [
  {
    id: "knowing-doing",
    category: "learn_cat_core_concept",
    color: "blue",
    title: "learn_blog_1_title",
    subtitle: "learn_blog_1_subtitle",
    readTime: "4 min",
    heroIcon: "\u{1F309}",
    content: "learn_blog_1_content",
    keyInsight: "learn_blog_1_insight",
    sources: "Schwartz & Sharpe (2006); Grossmann et al. (2020); Nusbaum (2025)",
  },
  {
    id: "wisdom-science",
    category: "learn_cat_the_science",
    color: "emerald",
    title: "learn_blog_2_title",
    subtitle: "learn_blog_2_subtitle",
    readTime: "5 min",
    heroIcon: "\u{1F9EC}",
    content: "learn_blog_2_content",
    keyInsight: "learn_blog_2_insight",
    sources: "Ardelt (2003); Grossmann et al. (2021); Jeste et al. (2019); Draganski & May (2008)",
  },
  {
    id: "three-dimensions",
    category: "learn_cat_deep_dive",
    color: "violet",
    title: "learn_blog_3_title",
    subtitle: "learn_blog_3_subtitle",
    readTime: "6 min",
    heroIcon: "\u{1F53A}",
    content: "learn_blog_3_content",
    keyInsight: "learn_blog_3_insight",
    sources: "Ardelt (2003); Grossmann et al. (2016); Jazaieri et al. (2014)",
  },
  {
    id: "autopilot",
    category: "learn_cat_behavioral",
    color: "amber",
    title: "learn_blog_4_title",
    subtitle: "learn_blog_4_subtitle",
    readTime: "4 min",
    heroIcon: "\u{2708}\u{FE0F}",
    content: "learn_blog_4_content",
    keyInsight: "learn_blog_4_insight",
    sources: "Grossmann et al. (2021); Kabat-Zinn (2003); Porter & Schumann (2018)",
  },
  {
    id: "mentoring",
    category: "learn_cat_application",
    color: "rose",
    title: "learn_blog_5_title",
    subtitle: "learn_blog_5_subtitle",
    readTime: "3 min",
    heroIcon: "\u{1F91D}",
    content: "learn_blog_5_content",
    keyInsight: "learn_blog_5_insight",
    sources: "Lundeberg & Yadav (2006); Epstein & Street (2011); Moon (2006)",
  },
  {
    id: "narrative",
    category: "learn_cat_method",
    color: "sky",
    title: "learn_blog_6_title",
    subtitle: "learn_blog_6_subtitle",
    readTime: "4 min",
    heroIcon: "\u{1F4D6}",
    content: "learn_blog_6_content",
    keyInsight: "learn_blog_6_insight",
    sources: "McAdams & McLean (2013); Butler (1963); Bauer & McAdams (2004)",
  },
  {
    id: "transformative-moments",
    category: "learn_cat_transformative",
    color: "blue",
    title: "learn_blog_7_title",
    subtitle: "learn_blog_7_subtitle",
    readTime: "5 min",
    heroIcon: "\u{1F4A1}",
    content: "learn_blog_7_content",
    keyInsight: "learn_blog_7_insight",
    sources: "Mezirow (1978/2018); Christie et al. (2015); Calleja (2014)",
  },
  {
    id: "complex-systems",
    category: "learn_cat_systems",
    color: "emerald",
    title: "learn_blog_8_title",
    subtitle: "learn_blog_8_subtitle",
    readTime: "5 min",
    heroIcon: "\u{1F300}",
    content: "learn_blog_8_content",
    keyInsight: "learn_blog_8_insight",
    sources: "Resnicow & Page (2008); Rusk & Waters (2018); Hayes et al. (2020)",
  },
  {
    id: "single-session",
    category: "learn_cat_intervention",
    color: "violet",
    title: "learn_blog_9_title",
    subtitle: "learn_blog_9_subtitle",
    readTime: "4 min",
    heroIcon: "\u{26A1}",
    content: "learn_blog_9_content",
    keyInsight: "learn_blog_9_insight",
    sources: "Pitt et al. (2020); Perreault et al. (2023); McMillon (2020)",
  },
  {
    id: "change-across-scales",
    category: "learn_cat_framework",
    color: "amber",
    title: "learn_blog_10_title",
    subtitle: "learn_blog_10_subtitle",
    readTime: "5 min",
    heroIcon: "\u{1F30D}",
    content: "learn_blog_10_content",
    keyInsight: "learn_blog_10_insight",
    sources: "James et al. (2020); Rusk & Waters (2018); Fuchs et al.",
  },
  {
    id: "narrative-identity-cas",
    category: "learn_cat_identity",
    color: "rose",
    title: "learn_blog_11_title",
    subtitle: "learn_blog_11_subtitle",
    readTime: "5 min",
    heroIcon: "\u{1F4DC}",
    content: "learn_blog_11_content",
    keyInsight: "learn_blog_11_insight",
    sources: "McAdams & McLean (2013); Kerr (2020); Piers (2005)",
  },
  {
    id: "why-clarynext-works",
    category: "learn_cat_platform",
    color: "sky",
    title: "learn_blog_12_title",
    subtitle: "learn_blog_12_subtitle",
    readTime: "6 min",
    heroIcon: "\u{1F3AF}",
    content: "learn_blog_12_content",
    keyInsight: "learn_blog_12_insight",
    sources: "Mezirow (2018); Hayes et al. (2020); Rusk & Waters (2018); Pitt et al. (2020)",
  },
];

const WISDOM_DIMENSIONS: WisdomDimension[] = [
  {
    name: "learn_dim_cognitive",
    icon: "\u{1F9E0}",
    color: "blue",
    description: "learn_dim_cognitive_desc",
    methods: ["learn_method_dialectical", "learn_method_humility", "learn_method_complexity"],
    question: "learn_dim_cognitive_q",
  },
  {
    name: "learn_dim_reflective",
    icon: "\u{1FA9E}",
    color: "violet",
    description: "learn_dim_reflective_desc",
    methods: ["learn_method_distanced", "learn_method_journaling", "learn_method_perspective"],
    question: "learn_dim_reflective_q",
  },
  {
    name: "learn_dim_affective",
    icon: "\u{1F49B}",
    color: "amber",
    description: "learn_dim_affective_desc",
    methods: ["learn_method_compassion", "learn_method_loving_kindness", "learn_method_prosocial"],
    question: "learn_dim_affective_q",
  },
];

const STATS: Stat[] = [
  { value: "745+", label: "learn_stat_sources", icon: "\u{1F4DA}" },
  { value: "$48.4B", label: "learn_stat_market", icon: "\u{1F4C8}" },
  { value: "3", label: "learn_stat_dimensions", icon: "\u{1F53A}" },
  { value: "8+", label: "learn_stat_disciplines", icon: "\u{1F52C}" },
];

const METHODS: Method[] = [
  { name: "learn_meth_distanced", effectiveness: 92, category: "learn_meth_cat_metacognitive", color: "blue" },
  { name: "learn_meth_perspective", effectiveness: 87, category: "learn_meth_cat_metacognitive", color: "violet" },
  { name: "learn_meth_single_session", effectiveness: 85, category: "learn_meth_cat_transformative", color: "rose" },
  { name: "learn_meth_contemplative", effectiveness: 84, category: "learn_meth_cat_mindfulness", color: "emerald" },
  { name: "learn_meth_problem_reframing", effectiveness: 83, category: "learn_meth_cat_cognitive_restructuring", color: "sky" },
  { name: "learn_meth_narrative", effectiveness: 80, category: "learn_meth_cat_reflective", color: "amber" },
  { name: "learn_meth_socratic", effectiveness: 79, category: "learn_meth_cat_dialogic", color: "blue" },
  { name: "learn_meth_compassion", effectiveness: 78, category: "learn_meth_cat_affective", color: "violet" },
  { name: "learn_meth_case_based", effectiveness: 76, category: "learn_meth_cat_experiential", color: "emerald" },
  { name: "learn_meth_experiential_challenge", effectiveness: 75, category: "learn_meth_cat_embodied", color: "amber" },
];

const METHOD_DEEP_DIVES: MethodDeepDive[] = [
  {
    title: "learn_deep_distanced_title",
    icon: "\u{1F52D}",
    color: "blue",
    description: "learn_deep_distanced_desc",
    howApplied: "learn_deep_distanced_applied",
    source: "Grossmann et al., 2021",
  },
  {
    title: "learn_deep_perspective_title",
    icon: "\u{1F441}\u{FE0F}",
    color: "violet",
    description: "learn_deep_perspective_desc",
    howApplied: "learn_deep_perspective_applied",
    source: "Galinsky & Moskowitz, 2000",
  },
  {
    title: "learn_deep_contemplative_title",
    icon: "\u{1F9D8}",
    color: "emerald",
    description: "learn_deep_contemplative_desc",
    howApplied: "learn_deep_contemplative_applied",
    source: "Kabat-Zinn, 2003; Tang et al., 2015",
  },
  {
    title: "learn_deep_narrative_title",
    icon: "\u{1F4D6}",
    color: "amber",
    description: "learn_deep_narrative_desc",
    howApplied: "learn_deep_narrative_applied",
    source: "McAdams & McLean, 2013",
  },
  {
    title: "learn_deep_single_session_title",
    icon: "\u{26A1}",
    color: "rose",
    description: "learn_deep_single_session_desc",
    howApplied: "learn_deep_single_session_applied",
    source: "Pitt et al., 2020; Perreault et al., 2023",
  },
  {
    title: "learn_deep_reframing_title",
    icon: "\u{1F504}",
    color: "sky",
    description: "learn_deep_reframing_desc",
    howApplied: "learn_deep_reframing_applied",
    source: "Mezirow, 2018; G et al., Solution-Oriented Brief Therapy",
  },
];

const SCHOLARLY_MODELS = [
  { name: "learn_model_ardelt", year: "2003", focus: "learn_model_ardelt_focus", color: "blue" as ColorKey },
  { name: "learn_model_berlin", year: "2000", focus: "learn_model_berlin_focus", color: "violet" as ColorKey },
  { name: "learn_model_sternberg", year: "1998", focus: "learn_model_sternberg_focus", color: "emerald" as ColorKey },
  { name: "learn_model_jeste", year: "2019", focus: "learn_model_jeste_focus", color: "amber" as ColorKey },
  { name: "learn_model_mezirow", year: "1978/2018", focus: "learn_model_mezirow_focus", color: "rose" as ColorKey },
  { name: "learn_model_synergistic", year: "2018", focus: "learn_model_synergistic_focus", color: "sky" as ColorKey },
  { name: "learn_model_wayshaping", year: "2020", focus: "learn_model_wayshaping_focus", color: "blue" as ColorKey },
  { name: "learn_model_cas", year: "2008", focus: "learn_model_cas_focus", color: "violet" as ColorKey },
];

type LearnSection = "explore" | "science" | "methods";

const fadeIn = {
  hidden: { opacity: 0, y: 12 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

// ─── Sub-Components ──────────────────────────────────────────────────────────

function StatCard({ stat, index, t }: { stat: Stat; index: number; t: (key: string) => string }) {
  return (
    <motion.div
      custom={index}
      variants={fadeIn}
      initial="hidden"
      animate="visible"
      className="rounded-2xl border border-border/50 bg-card/80 p-6 text-center transition-all duration-300 hover:shadow-md hover:shadow-black/[0.04]"
    >
      <div className="text-2xl mb-2">{stat.icon}</div>
      <div className="text-3xl font-extrabold text-primary leading-none tracking-tight">
        {stat.value}
      </div>
      <div className="text-[11px] text-muted-foreground mt-2 tracking-wide leading-snug">
        {t(stat.label)}
      </div>
    </motion.div>
  );
}

function BlogCard({
  post,
  onClick,
  t,
}: {
  post: BlogPost;
  onClick: () => void;
  t: (key: string) => string;
}) {
  const styles = COLOR_STYLES[post.color];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      onClick={onClick}
      className="group cursor-pointer rounded-2xl border border-border/50 bg-card/80 p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/[0.06] flex flex-col min-h-[220px]"
    >
      <div className="flex justify-between items-center mb-3">
        <span className={cn("px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider", styles.badge)}>
          {t(post.category)}
        </span>
        <span className="text-[11px] text-muted-foreground/60">{post.readTime}</span>
      </div>
      <div className="text-3xl mb-3">{post.heroIcon}</div>
      <h3 className="text-base font-bold text-foreground leading-snug mb-1.5">
        {t(post.title)}
      </h3>
      <p className="text-[13px] text-muted-foreground leading-relaxed flex-1">
        {t(post.subtitle)}
      </p>
      <div className={cn("mt-4 text-[13px] font-semibold flex items-center gap-1.5", styles.text)}>
        {t("learn_read_article")}
        <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
      </div>
    </motion.div>
  );
}

function WisdomDimensionCard({
  dim,
  isActive,
  onClick,
  t,
}: {
  dim: WisdomDimension;
  isActive: boolean;
  onClick: () => void;
  t: (key: string) => string;
}) {
  const styles = COLOR_STYLES[dim.color];

  return (
    <div
      onClick={onClick}
      className={cn(
        "rounded-2xl border p-5 cursor-pointer transition-all duration-300",
        isActive
          ? cn(styles.bgSubtle, styles.border, "scale-[1.01] shadow-sm")
          : "bg-card/80 border-border/50 hover:border-border"
      )}
    >
      <div className="flex items-center gap-3 mb-3">
        <span className="text-2xl">{dim.icon}</span>
        <span className="text-lg font-bold text-foreground">{t(dim.name)}</span>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed mb-3">
        {t(dim.description)}
      </p>
      {isActive && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <p className={cn("text-[13px] font-semibold italic mb-2", styles.text)}>
            &ldquo;{t(dim.question)}&rdquo;
          </p>
          <div className="flex flex-wrap gap-1.5">
            {dim.methods.map((m) => (
              <span
                key={m}
                className={cn("px-3 py-1 rounded-full text-[11px] font-semibold", styles.badge)}
              >
                {t(m)}
              </span>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}

function MethodBar({
  method,
  index,
  t,
}: {
  method: Method;
  index: number;
  t: (key: string) => string;
}) {
  const [width, setWidth] = useState(0);
  const styles = COLOR_STYLES[method.color];

  useEffect(() => {
    const timer = setTimeout(() => setWidth(method.effectiveness), 200 + index * 100);
    return () => clearTimeout(timer);
  }, [method.effectiveness, index]);

  return (
    <div className="mb-3.5">
      <div className="flex justify-between mb-1.5 items-baseline">
        <span className="text-[13px] font-semibold text-foreground">{t(method.name)}</span>
        <span className="text-[11px] text-muted-foreground/60 italic">{t(method.category)}</span>
      </div>
      <div className="h-2 bg-muted/60 rounded-full overflow-hidden">
        <div
          className={cn("h-full rounded-full bg-gradient-to-r transition-all duration-[1200ms] ease-out", styles.barGradient)}
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}

function ArticleView({
  post,
  onBack,
  t,
}: {
  post: BlogPost;
  onBack: () => void;
  t: (key: string) => string;
}) {
  const navigate = useNavigate();
  const styles = COLOR_STYLES[post.color];
  const content = t(post.content);
  const paragraphs = content.split("\n\n");

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="max-w-2xl mx-auto"
    >
      <Button
        variant="outline"
        size="sm"
        onClick={onBack}
        className="mb-6 rounded-full gap-1.5"
      >
        <ArrowLeft className="w-3.5 h-3.5 rtl:rotate-180" />
        {t("learn_back")}
      </Button>

      <span className={cn("inline-block px-3.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider", styles.badge)}>
        {t(post.category)}
      </span>

      <h1 className="text-2xl md:text-3xl font-extrabold text-foreground leading-tight mt-4 mb-2">
        {post.heroIcon} {t(post.title)}
      </h1>

      <p className="text-base text-muted-foreground leading-relaxed italic mb-6">
        {t(post.subtitle)}
      </p>

      <div className="flex gap-4 text-xs text-muted-foreground/60 mb-6">
        <span>{post.readTime}</span>
        <span>{t("learn_evidence_based")}</span>
      </div>

      <div className="border-t border-border/50 pt-6 space-y-4">
        {paragraphs.map((para, i) => (
          <p key={i} className="text-[15px] text-foreground/90 leading-[1.8]">
            {para}
          </p>
        ))}
      </div>

      {/* Key Insight */}
      <div className={cn("rounded-2xl p-6 mt-8", styles.bgSubtle, "border", styles.border)}>
        <div className={cn("text-xs font-bold uppercase tracking-widest mb-2", styles.text)}>
          {t("learn_key_insight")}
        </div>
        <p className="text-base text-foreground leading-relaxed italic">
          {t(post.keyInsight)}
        </p>
      </div>

      {/* Sources */}
      <div className="text-xs text-muted-foreground/60 mt-6 pt-4 border-t border-border/50">
        <strong className="text-muted-foreground">{t("learn_based_on_research")}</strong>{" "}
        {post.sources}
      </div>

      {/* CTA */}
      <div className="rounded-2xl bg-gradient-to-br from-primary/[0.06] to-violet-500/[0.04] border border-primary/20 p-6 mt-8 text-center">
        <p className="text-sm text-foreground mb-4 leading-relaxed">
          {t("learn_cta_ready")}
        </p>
        <Button
          onClick={() => navigate("/marketplace")}
          className="rounded-full px-8"
        >
          {t("learn_cta_explore")}
          <ArrowRight className="w-4 h-4 ms-2 rtl:rotate-180" />
        </Button>
      </div>
    </motion.div>
  );
}

// ─── Triangle Visualization ──────────────────────────────────────────────────

function WisdomTriangle({
  activeDimension,
  onSelect,
}: {
  activeDimension: number;
  onSelect: (i: number) => void;
}) {
  const { t } = useTranslation();
  const vertices = [
    { cx: 200, cy: 40, color: "#3b82f6", label: t("learn_tri_cognitive"), icon: "\u{1F9E0}", sub: t("learn_tri_cognitive_sub") },
    { cx: 60, cy: 290, color: "#8b5cf6", label: t("learn_tri_reflective"), icon: "\u{1FA9E}", sub: t("learn_tri_reflective_sub") },
    { cx: 340, cy: 290, color: "#f59e0b", label: t("learn_tri_affective"), icon: "\u{1F49B}", sub: t("learn_tri_affective_sub") },
  ];

  return (
    <svg viewBox="0 0 400 340" className="max-w-[340px] w-full mx-auto">
      <polygon
        points="200,40 60,290 340,290"
        fill="none"
        stroke="currentColor"
        className="text-border"
        strokeWidth="1.5"
      />
      <polygon points="200,40 60,290 340,290" fill="currentColor" className="text-primary/[0.03]" />

      {/* Dashed lines to center */}
      <line x1="200" y1="40" x2="200" y2="190" stroke="#3b82f620" strokeWidth="1" strokeDasharray="4,4" />
      <line x1="60" y1="290" x2="200" y2="190" stroke="#8b5cf620" strokeWidth="1" strokeDasharray="4,4" />
      <line x1="340" y1="290" x2="200" y2="190" stroke="#f59e0b20" strokeWidth="1" strokeDasharray="4,4" />

      {/* Center */}
      <circle cx="200" cy="190" r="26" fill="#3b82f610" stroke="#3b82f640" strokeWidth="1.5" />
      <text x="200" y="186" textAnchor="middle" fontSize="8" fill="#64748b" fontWeight="600">
        {t("learn_tri_actionable")}
      </text>
      <text x="200" y="198" textAnchor="middle" fontSize="8" fill="#64748b" fontWeight="600">
        {t("learn_tri_wisdom")}
      </text>

      {/* Vertices */}
      {vertices.map((v, i) => (
        <g key={v.label} className="cursor-pointer" onClick={() => onSelect(i)}>
          <circle
            cx={v.cx}
            cy={v.cy}
            r={activeDimension === i ? 22 : 18}
            fill={`${v.color}15`}
            stroke={v.color}
            strokeWidth={activeDimension === i ? 2 : 1}
            className="transition-all duration-300"
          />
          <text x={v.cx} y={v.cy + 1} textAnchor="middle" fontSize="14" dominantBaseline="central">
            {v.icon}
          </text>
          <text
            x={v.cx}
            y={v.cy + (v.cy < 100 ? -28 : 36)}
            textAnchor="middle"
            fontSize="10"
            fill={v.color}
            fontWeight="700"
          >
            {v.label}
          </text>
          <text
            x={v.cx}
            y={v.cy + (v.cy < 100 ? -17 : 47)}
            textAnchor="middle"
            fontSize="8"
            fill="#94a3b8"
          >
            {v.sub}
          </text>
        </g>
      ))}
    </svg>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function LearnTabContent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<LearnSection>("explore");
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [activeDimension, setActiveDimension] = useState(0);

  const sections: { id: LearnSection; label: string }[] = [
    { id: "explore", label: t("learn_nav_explore") },
    { id: "science", label: t("learn_nav_science") },
    { id: "methods", label: t("learn_nav_methods") },
  ];

  // If viewing an article, show article view
  if (selectedPost) {
    return (
      <ArticleView
        post={selectedPost}
        onBack={() => setSelectedPost(null)}
        t={t}
      />
    );
  }

  return (
    <section className="space-y-6">
      {/* Header + Sub-Navigation */}
      <div className="text-center space-y-4">
        <div>
          <h2 className="text-xl md:text-2xl font-extrabold text-foreground leading-tight">
            {t("learn_title")}
          </h2>
          <p className="text-sm text-muted-foreground mt-1.5 max-w-lg mx-auto leading-relaxed">
            {t("learn_subtitle")}
          </p>
        </div>

        {/* Section tabs */}
        <div className="inline-flex gap-1 bg-muted/50 rounded-full p-1 border border-border/40">
          {sections.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              className={cn(
                "px-5 py-2 rounded-full text-[13px] font-semibold transition-all duration-200",
                activeSection === s.id
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── EXPLORE ── */}
      {activeSection === "explore" && (
        <motion.div
          key="explore"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="space-y-8"
        >
          {/* Stats */}
      {/*    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {STATS.map((s, i) => (
              <StatCard key={s.label} stat={s} index={i} t={t} />
            ))}
          </div>*/}

          {/* Blog Grid */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground/60 mb-4">
              {t("learn_featured_insights")}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {BLOG_POSTS.map((post) => (
                <BlogCard
                  key={post.id}
                  post={post}
                  onClick={() => setSelectedPost(post)}
                  t={t}
                />
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* ── THE SCIENCE ── */}
      {activeSection === "science" && (
        <motion.div
          key="science"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="space-y-8"
        >
          {/* Intro */}
          <div className="text-center">
            <h3 className="text-xl font-extrabold text-foreground mb-2">
              {t("learn_science_title")}
            </h3>
            <p className="text-sm text-muted-foreground max-w-lg mx-auto leading-relaxed">
              {t("learn_science_subtitle")}
            </p>
          </div>

          {/* Dimension Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {WISDOM_DIMENSIONS.map((dim, i) => (
              <WisdomDimensionCard
                key={dim.name}
                dim={dim}
                isActive={activeDimension === i}
                onClick={() => setActiveDimension(i)}
                t={t}
              />
            ))}
          </div>

          {/* Triangle Visualization */}
          <div className="rounded-2xl border border-border/50 bg-card/80 p-6 text-center">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground/60 mb-5">
              {t("learn_wisdom_model")}
            </h4>
            <WisdomTriangle
              activeDimension={activeDimension}
              onSelect={setActiveDimension}
            />
            <p className="text-[13px] text-muted-foreground/60 mt-4 italic max-w-md mx-auto">
              {t("learn_wisdom_model_desc")}
            </p>
          </div>

          {/* Scholarly Models */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground/60 mb-4">
              {t("learn_scholarly_models")}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {SCHOLARLY_MODELS.map((model) => {
                const styles = COLOR_STYLES[model.color];
                return (
                  <div
                    key={model.name}
                    className={cn(
                      "rounded-xl border border-border/50 bg-card/80 p-4 border-s-[3px]",
                      styles.borderAccent
                    )}
                  >
                    <div className="text-sm font-bold text-foreground mb-0.5">
                      {t(model.name)}
                    </div>
                    <div className={cn("text-[11px] mb-1.5", styles.text)}>{model.year}</div>
                    <div className="text-xs text-muted-foreground leading-relaxed">
                      {t(model.focus)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}

      {/* ── METHODS ── */}
      {activeSection === "methods" && (
        <motion.div
          key="methods"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="space-y-8"
        >
          {/* Intro */}
          <div className="text-center">
            <h3 className="text-xl font-extrabold text-foreground mb-2">
              {t("learn_methods_title")}
            </h3>
            <p className="text-sm text-muted-foreground max-w-lg mx-auto leading-relaxed">
              {t("learn_methods_subtitle")}
            </p>
          </div>

          {/* Method Bars */}
          <div className="rounded-2xl border border-border/50 bg-card/80 p-6">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground/60 mb-5">
              {t("learn_research_support")}
            </h4>
            {METHODS.map((m, i) => (
              <MethodBar key={m.name} method={m} index={i} t={t} />
            ))}
            <p className="text-[11px] text-muted-foreground/50 mt-4 italic">
              {t("learn_research_support_note")}
            </p>
          </div>

          {/* Method Deep Dives */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {METHOD_DEEP_DIVES.map((method) => {
              const styles = COLOR_STYLES[method.color];
              return (
                <div
                  key={method.title}
                  className={cn(
                    "rounded-2xl border border-border/50 bg-card/80 p-6 border-t-[3px]",
                    styles.borderAccent
                  )}
                >
                  <div className="text-2xl mb-3">{method.icon}</div>
                  <h4 className="text-base font-bold text-foreground mb-2">
                    {t(method.title)}
                  </h4>
                  <p className="text-[13px] text-muted-foreground leading-relaxed mb-4">
                    {t(method.description)}
                  </p>
                  <div className={cn("rounded-xl p-3.5 mb-3", styles.bgSubtle, "border", styles.border)}>
                    <div className={cn("text-[10px] font-bold uppercase tracking-widest mb-1", styles.text)}>
                      {t("learn_how_applied")}
                    </div>
                    <p className="text-xs text-foreground/85 leading-relaxed">
                      {t(method.howApplied)}
                    </p>
                  </div>
                  <div className="text-[11px] text-muted-foreground/50 italic">{method.source}</div>
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <div className="rounded-2xl bg-gradient-to-br from-primary/[0.06] to-violet-500/[0.04] border border-primary/20 p-8 text-center">
            <h3 className="text-lg font-extrabold text-foreground mb-2">
              {t("learn_cta_title")}
            </h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto mb-5 leading-relaxed">
              {t("learn_cta_desc")}
            </p>
            <Button
              onClick={() => navigate("/marketplace")}
              className="rounded-full px-8"
            >
              {t("learn_cta_try")}
              <ArrowRight className="w-4 h-4 ms-2 rtl:rotate-180" />
            </Button>
          </div>
        </motion.div>
      )}
    </section>
  );
}
